import React from 'react';
import { Helmet } from 'react-helmet-async';
import { organizationData } from '@/data/organizationSchema';
import { useI18n } from '@/contexts/I18nContext';

interface EnhancedOrganizationSchemaProps {
  overrideData?: Partial<typeof organizationData>;
}

const LOCALE_TO_BCP47: Record<string, string> = {
  es: 'es-ES',
  de: 'de-CH',
  fr: 'fr-CH',
  it: 'it-CH',
  pt: 'pt-PT',
  en: 'en-US',
};

/**
 * EnhancedOrganizationSchema - Comprehensive Organization JSON-LD
 *
 * Renders the full organization schema (logo, address, contactPoint, sameAs,
 * hasOfferCatalog, openingHours, geo, aggregateRating, reviews, awards,
 * payment, currencies, serviceArea, knowsAbout, slogan) with i18n awareness
 * via `inLanguage`. Designed to be mounted ONCE globally (in App.tsx) to avoid
 * JSON-LD duplication across pages.
 */
const EnhancedOrganizationSchema: React.FC<EnhancedOrganizationSchemaProps> = ({
  overrideData = {},
}) => {
  // Safe i18n read: this component is mounted inside I18nProvider in App.tsx,
  // but we guard for any edge case (tests / isolated renders).
  let locale = 'es';
  try {
    locale = useI18n().locale ?? 'es';
  } catch {
    locale = 'es';
  }

  const data = { ...organizationData, ...overrideData };

  // Nodo aditivo: comparte @id con el Organization estático de index.html para que
  // Google fusione ambos. Solo añade propiedades que NO están en el estático, evitando
  // valores contradictorios (legalName, foundingDate, dirección, contactPoint, sameAs...).
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'ProfessionalService'],
    '@id': `${data.url}/#organization`,
    name: data.name,
    url: data.url,
    email: (data as any).email,
    hasMap: (data as any).hasMap,
    slogan: data.slogan,
    priceRange: data.priceRange,
    paymentAccepted: data.paymentAccepted,
    currenciesAccepted: data.currenciesAccepted,
    knowsAbout: data.knowsAbout,
    award: data.award,
    inLanguage: LOCALE_TO_BCP47[locale] ?? 'es-ES',
    hasOfferCatalog: data.hasOfferCatalog,
    geo: {
      '@type': 'GeoCoordinates',
      ...data.geo,
    },
    serviceArea: data.serviceArea,
    // aggregateRating se emite desde <ReviewsSchema /> con datos reales de Google
    // para evitar valores duplicados/contradictorios bajo el mismo @id.
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default EnhancedOrganizationSchema;
